// @givmo/auth — the WEB-SUITE session primitives (AS-SUITE4, D-AS-13 option D).
//
// Sign in once on any `*.givmo.io` property and every other suite property finds you already signed
// in. Three calls against the backend's unauthenticated `/authn` mount, and this module is the ONLY
// place in the repo that names them — `suite-session-rail.test.ts` is what keeps that true:
//
//   establish  POST /authn/session/login    ID token in `Authorization: Bearer` → `__Host-gsuite` cookie
//   adopt      POST /authn/session/handoff  the cookie → a Firebase custom token → signInWithCustomToken
//   release    POST /authn/session/logout   delete the row, clear the cookie
//
// ⚠ "Suite" here is the WEB suite — the `*.givmo.io` single-page apps — and has nothing to do with
// the backend's suite DATA PLANE. Same word, two programs.
//
// ── Why this module does its own `fetch` instead of taking a transport port ──────────────────────
//
// `passkeys.ts` takes a `PasskeyAssertionTransport` port and lets each app supply the adapter, which
// is this package's usual shape. Two facts rule it out here, and both are load-bearing:
//
//   1. **`@givmo/api-client` cannot send the cookie.** Its generated fetch client sets no
//      `credentials`, so every call defaults to `same-origin` — and every suite property is a
//      DIFFERENT origin from the API host. A port implemented over `givmoClient` would silently send
//      no cookie and the handoff would answer "no session" forever, in a way that looks like the
//      backend being dark.
//   2. **A port would put the three route paths in five app adapters.** The one invariant this
//      mechanism creates is that the suite cookie is honoured by these routes and by nothing else;
//      the client half of that is "one module knows these paths". The rail enforces it, and a port
//      would make the rail unenforceable by construction.
//
// So the paths, the credential mode and the App Check header live here, once. The `fetch` seam is a
// test injection point, not an app-supplied adapter.
//
// ── Silence is a requirement, not an oversight ───────────────────────────────────────────────────
//
// Nothing here logs. While `SUITE_SILENT_SSO_ENABLED` is off — which is every deployed environment
// today — all three routes 404, so `adoptSuiteSession` gets a 404 on EVERY cold boot of EVERY
// property. A console line there would print in every user's console on every page load in
// production. `agent_docs/auth.md` already records this rule for the passkey probe, which 404s for
// exactly the same reason; the outcome is returned as a value instead, which is both testable and
// useful to a caller. `@givmo/auth` also depends on no logger — `@givmo/observability` is a separate
// package this one does not take — so "log" would mean `console.warn`, i.e. the banned thing.
//
// ── Every failure resolves to "no suite session" ─────────────────────────────────────────────────
//
// C7 in the ADR. A dark 404, a refused `Origin`, a 429, a timeout, malformed JSON, a fetch that
// throws, an App Check mint that fails — all of them resolve to the same thing: the property has no
// suite session and renders its own door, which is the behaviour shipped today. Nothing in this
// module ever throws out of the chokepoint that calls it.
//
// ── Every wait a user can see is bounded; the one wait that must not be a race is not one ────────
//
// Two things a user waits on run through here: the boot handoff (in front of first paint) and the
// release (in front of the local sign-out). Every leg of both carries a timer, the App Check mint
// included (AS-SUITE11). The sign-out's other wait — for a login that may still land — is NOT a
// timer: a timer cannot know whether the login it stopped waiting for will set the cookie after the
// release. It is a cancel handle ({@link createSuiteSessionCancelHandle}), which stops a login that
// has not been sent and waits only for one that has, whose own write timeout bounds it.

import { signInWithCustomToken, type Auth, type User, type UserCredential } from 'firebase/auth';
import { applySessionPersistence } from './session';

/** `POST` — establish this browser's suite session from a just-completed sign-in. */
const LOGIN_PATH = '/authn/session/login';
/** `POST` — exchange this browser's suite cookie for a Firebase custom token. */
const HANDOFF_PATH = '/authn/session/handoff';
/** `POST` — end this browser's suite session. Idempotent. */
const LOGOUT_PATH = '/authn/session/logout';

/**
 * The App Check header the backend verifies in-handler (the `/authn` mount inherits none).
 *
 * Requiring it is also what makes the request non-simple, forcing a CORS preflight that fails for a
 * non-allowlisted origin — so a hostile page's POST never fires at all.
 */
const APP_CHECK_HEADER = 'X-Firebase-AppCheck';

/**
 * The marker claim the handoff mint stamps unconditionally (`gvm_handoff`, backend
 * `app/domain/session_assurance.py`).
 *
 * {@link establishSuiteSession} refuses to run on a token carrying it, and refuses **regardless of
 * freshness** — the check sits AHEAD of the freshness bound below, deliberately. A handed-off
 * session's assurance is a MIRROR of the session it came from (plan §3 law 8); establishing from one
 * would mint a suite session out of a copy and launder the origin's assurance into a new snapshot.
 * The backend refuses it uniformly for the same reason (AS-SUITE3 review finding F1, folded on the
 * SUITE3 branch after `678504bf0`: a token that is itself a handoff is now always refused at
 * `login`, where a FRESH one could previously 200). So this is not a client optimisation shadowing a
 * server rule — both ends refuse, and this end refuses first so the refusal costs no request on a
 * per-IP budget shared with real sign-ins.
 *
 * ⚠ Key on the MARKER, never on "does this token carry `gvm_*` claims". The marker is stamped
 * unconditionally precisely because the mirror cannot be inferred from the other keys: a handoff
 * whose origin had sparse assurance mirrors nothing else and would read as a direct session.
 */
const HANDOFF_CLAIM = 'gvm_handoff';

/**
 * How old a sign-in may be and still start a suite session — the client half of the backend's
 * `SUITE_SESSION_LOGIN_MAX_AUTH_AGE_SECONDS` (300s).
 *
 * ⚠ This is a LAW, not a performance guard. It is what stops an hour-old ID token being upgraded
 * into a fortnight-long suite session, and it is why `establishSuiteSession` is safe to call from an
 * auth-state chokepoint that also fires on cold boot with a persisted Firebase session: a restored
 * session is not a sign-in, and the backend would refuse it. Checking here means the refusal costs
 * no request rather than one per boot per property.
 *
 * Kept deliberately EQUAL to the backend value rather than shorter. A shorter client bound would
 * silently drop legitimate sign-ins the backend would have accepted; the backend is the authority
 * and this is a pre-flight copy of its rule, so they must agree.
 */
const MAX_AUTH_AGE_MS = 300_000;

/**
 * Default bound on EACH leg of the handoff — the App Check mint, then the request — in milliseconds.
 *
 * This call sits IN FRONT of the property's `isReady` gate — in front of first paint for a
 * signed-out visitor — so the number is a hard bound on how long someone stares at a splash before
 * their own door appears. The backend leg is one indexed row read plus one Firebase custom-token
 * mint; 4s absorbs a cold Cloud Run instance and a slow mobile network with room to spare. Shorter
 * (≤2s) makes a cold start false-negative into "no session", which is the WRONG failure — it shows a
 * door to someone who is signed in, and reads as the feature being broken. Longer (≥6s) makes a
 * black-holed network read as a hung app.
 *
 * ⚠ The mint leg is bounded too (AS-SUITE11), and it is not a formality. Adoption is the path most
 * likely to mint COLD — App Check caches its token per origin, and a property booting signed-out
 * while a suite session exists is usually one this browser has not just used — and
 * `@firebase/app-check@0.12.0` bounds nothing on that road: the reCAPTCHA Enterprise script is
 * loaded with an `onload` and no `onerror`, and the token exchange is a bare `fetch` with no
 * signal. A blocked script therefore leaves the mint pending forever, and before the bound a
 * signed-out boot sat on its splash with it, wherever App Check is configured. Past the bound the
 * mint is a failed mint: no request, "no session", the door. Worst case to the door ≈ 8 s (a mint
 * just inside its bound, then a black-holed handoff); a mint that never answers shows the door at
 * 4 s having sent nothing.
 */
const DEFAULT_ADOPT_TIMEOUT_MS = 4_000;

/**
 * Default bound on the login's and the release's REQUEST, and on the release's App Check mint.
 *
 * A sign-out waits on these (AS-SUITE9, AS-SUITE11): on a login already on the wire — the cancel
 * handle waits for nothing else — and on the release. The release mints in parallel with that wait,
 * so the sign-out's worst case is two of these: ≈ 16 s to the local `firebaseSignOut`, from a login
 * sent just before the click and black-holed, then a black-holed release.
 *
 * The establish's own App Check mint and ID-token refresh deliberately carry NO bound here. Nothing
 * waits on them any more — a sign-out that finds a login not yet sent stops it and moves on — and a
 * bound would only turn a slow-but-working cold mint into a missing suite session, silently: the
 * chokepoint's once-per-uid guard never retries an ordinary sign-in.
 */
const DEFAULT_WRITE_TIMEOUT_MS = 8_000;

/** The `fetch` implementation a caller may substitute. Defaults to the platform's. */
export type SuiteSessionFetch = typeof globalThis.fetch;

/**
 * An App Check token source — the app's EXISTING provider, passed straight through.
 *
 * The shipped shape is `firebaseAppCheckProvider(appCheck).getToken` (each app's `src/lib/api-client.ts`),
 * and its three behaviours are all meaningful here:
 *
 * - resolves a token → sent as `X-Firebase-AppCheck`;
 * - resolves `null` → App Check is not configured for this app (local/dev has no site key), so the
 *   header is omitted exactly as the api-client middleware omits it, and the backend's own
 *   `local`/`dev` skip covers it;
 * - **throws** → App Check IS configured and could not mint. Fail closed: the call is abandoned and
 *   the outcome is "no suite session". `getGivmoAppCheckToken` throws by design rather than minting
 *   an empty token, and this module must not undo that by sending unattested.
 */
export type SuiteAppCheckTokenSource = () => Promise<string | null>;

/** What every suite-session call needs to reach the API host. */
export interface SuiteSessionTransport {
  /** The API base URL — the app's own `env.apiBaseUrl`, the same value `@givmo/api-client` is configured with. */
  baseUrl: string;
  /** The app's App Check token source. Omit only where the app has no App Check at all. */
  appCheckToken?: SuiteAppCheckTokenSource;
  /** Test seam. Production leaves this unset and the platform `fetch` runs. */
  fetch?: SuiteSessionFetch;
  /**
   * Bound on each timed leg of the call, in milliseconds: the request, and — for adopt and release,
   * the two a user waits on — the App Check mint ahead of it. Defaults per operation; see the
   * constants above.
   */
  timeoutMs?: number;
}

/** What {@link establishSuiteSession} did, for tests and for a caller that wants to know. */
export type SuiteSessionEstablishOutcome =
  /** The backend recorded a suite session and set the cookie. */
  | 'established'
  /** Deliberately not attempted — no user, a handed-off session, or a sign-in too old to qualify. No request was sent. */
  | 'skipped'
  /** The sign-in ended first: its cancel handle was cancelled before the login could be sent, and none was. */
  | 'cancelled'
  /** Attempted and did not land: the routes are dark, the caller was refused, or the wire failed. */
  | 'unavailable';

/**
 * The cancel handle on a sign-in's establish (AS-SUITE11) — what lets a sign-out stop a login it
 * cannot see without waiting for the legs in front of it.
 *
 * A property's `signOut` must never release while a login is still able to land: the release
 * deletes nothing, the login then sets the cookie, and the person is "signed out" with a live suite
 * session behind them — on a shared machine, the next person signed in as them (AS-SUITE9). Waiting
 * for the whole establish closes that and costs the ID-token refresh (up to 30 s on the web) and an
 * App Check mint (unbounded) in front of every sign-out clicked just after a sign-in. Waiting with a
 * timer instead reopens it: a login whose token refresh finishes after the timer fires is SENT after
 * the release. The handle is the third shape:
 *
 * - a login this handle has NOT admitted never will be once it is cancelled — whichever leg it is
 *   waiting on, the token refresh, the mint, or a bootstrap chained in front of it;
 * - a login it HAS admitted is already on the wire, and {@link cancel} waits for it to settle —
 *   bounded by that request's own write timeout (8 s), which aborts it;
 * - so when `cancel()` resolves, no login of this sign-in is in flight and none can start. The
 *   release that follows it can be neither overtaken nor preceded by one.
 *
 * **Why no login can slip between the two**: a send is admitted in `send`, where the check of the
 * handle and the `fetch()` that puts the request on the wire are ONE synchronous block; `cancel`
 * sets the flag and takes its list of admitted requests in ONE synchronous block. JavaScript runs
 * each block to completion, so every login either ran its block first — and is on the list `cancel`
 * waits for — or runs it after, sees the flag, and never calls `fetch`. There is no third order.
 *
 * One handle spans a sign-in and everything that establishes for it — the chokepoint's attempt, the
 * post-provisioning one, a retried bootstrap's — so a sign-out stops all of them at once. Mint one
 * with {@link createSuiteSessionCancelHandle}; a structural look-alike admits nothing.
 */
export interface SuiteSessionCancelHandle {
  /** `true` once {@link cancel} has been called. No login gated by this handle can start after that. */
  readonly cancelled: boolean;
  /**
   * End this sign-in's establish: nothing not yet sent will be; resolves once every login already
   * on the wire has settled. Idempotent — every call answers the first call's promise — and never
   * rejects.
   */
  cancel(): Promise<void>;
}

export interface EstablishSuiteSessionOptions extends SuiteSessionTransport {
  /** The user whose just-completed sign-in this is. `null` is accepted and answers `skipped`, so a
   * chokepoint can hand its emission straight through without a guard of its own. */
  user: User | null;
  /**
   * Mirror of the property's own persistence choice — `false` yields a browser-SESSION cookie and
   * the short server-side TTL. Defaults to `true`, matching both the backend default and Firebase's
   * default persistence.
   *
   * ⚠ Passing `true` for a user who UNCHECKED "remember me" is a real defect, not a rounding error:
   * `true` emits a cookie with `Max-Age=1209600`, which survives a browser restart, so on a shared
   * computer the next person to open a suite property would be silently signed in as them. The door
   * that owns the checkbox is the only thing that knows; it must pass what the user chose.
   */
  rememberMe?: boolean;
  /**
   * The sign-in's {@link SuiteSessionCancelHandle}. With one, the login is sent only while it is
   * uncancelled, and a login it admitted is one its `cancel` waits for. Every property context
   * passes one; omitting it makes the login unstoppable, which only a caller with no sign-out may do.
   */
  cancelHandle?: SuiteSessionCancelHandle;
}

export interface AdoptSuiteSessionOptions extends SuiteSessionTransport {
  /** The property's Firebase `Auth` instance. */
  auth: Auth;
}

export interface ReleaseSuiteSessionOptions extends SuiteSessionTransport {
  /**
   * The ending sign-in's cancel handle — the one its establishes were gated by. Cancelled FIRST, the
   * moment the release is called; the release is sent only once every login it admitted has
   * settled. `null`/absent when no sign-in was observed.
   */
  cancelHandle?: SuiteSessionCancelHandle | null;
}

/**
 * Per-handle state, kept OFF the handle object — the same discipline `errors.ts`'s `WeakMap` keeps
 * for a raw error: nothing outside this module can re-open a cancelled gate or put a login on the
 * wire around it, and a look-alike object that was never minted here has no entry, so `send`
 * admits nothing through it (fail closed).
 */
interface SendGate {
  cancelled: boolean;
  /** The admitted logins that have not settled. Frozen once `cancelled` is set — `send` admits nothing after. */
  readonly onWire: Set<Promise<unknown>>;
}

const SEND_GATES = new WeakMap<SuiteSessionCancelHandle, SendGate>();

/**
 * Mint the {@link SuiteSessionCancelHandle} for one sign-in. A property creates one when its
 * chokepoint observes a sign-in, passes it to every establish for that sign-in, and hands it to
 * {@link releaseSuiteSession} when the sign-in ends.
 */
export function createSuiteSessionCancelHandle(): SuiteSessionCancelHandle {
  const gate: SendGate = { cancelled: false, onWire: new Set() };
  let settled: Promise<void> | null = null;
  const handle: SuiteSessionCancelHandle = {
    get cancelled() {
      return gate.cancelled;
    },
    cancel() {
      // ONE synchronous block: the flag and the list are taken together, so no admission can fall
      // between them (see the interface). `allSettled` because a login that failed or was aborted
      // has settled too — the wait is for the wire to be quiet, not for the login to succeed.
      gate.cancelled = true;
      settled ??= Promise.allSettled([...gate.onWire]).then(() => undefined);
      return settled;
    },
  };
  SEND_GATES.set(handle, gate);
  return handle;
}

export interface EstablishSuiteSessionAfterProvisioningOptions extends EstablishSuiteSessionOptions {
  /**
   * The establish the auth-state chokepoint already fired for THIS sign-in — an attempt that ran
   * on the Firebase emission before the Givmo user existed — or `null` when none did: the
   * chokepoint deliberately skipped the emission because a sign-up ceremony was marked in flight
   * (AS-SUITE9, the common case), it has not seen this uid, or the property carries no transport.
   * Its outcome decides whether a request is sent at all: `established` means the row existed
   * after all and the session is already there; anything else — `null` above all, the backend's
   * no-user-row refusal read as `unavailable`, a wire failure likewise — earns exactly one attempt
   * now that the row exists.
   */
  attempted: Promise<SuiteSessionEstablishOutcome> | null;
}

/**
 * Establish this browser's suite session from a sign-in that just completed.
 *
 * Call it from the property's auth-state chokepoint on every non-null emission. That covers EVERY
 * completed sign-in — email, the MFA-resolved continuation, OAuth by popup and by redirect return,
 * the passkey custom token, the SSO custom token — without enumerating call sites, because it is the
 * one place all of them arrive. The two guards below are what make that safe: a chokepoint also
 * fires on cold boot with a persisted session, and on the adoption this module performs itself.
 *
 * **Never blocks the sign-in that just happened.** The door already succeeded; a suite session is a
 * convenience layered on top. Callers use `void establishSuiteSession({…})` and never await it.
 *
 * Order of work, and every step's refusal:
 *
 *  1. force-refresh the ID token (`getIdTokenResult(true)`). Force, because a cached token can be
 *     the one held BEFORE this sign-in — after an MFA resolution or a custom-token exchange the
 *     cache is a lap behind, and the backend would verify the wrong session. (It does not make
 *     `auth_time` newer: `auth_time` is the authentication instant and survives every refresh. That
 *     is exactly what step 3 relies on.) A failure here resolves `unavailable`;
 *  2. `gvm_handoff` present → `skipped`, whatever its `auth_time`. An adopted session never
 *     re-establishes — see {@link HANDOFF_CLAIM}; the backend refuses it uniformly too;
 *  3. `auth_time` older than {@link MAX_AUTH_AGE_MS} → `skipped`, with no request sent. Elapsed-time
 *     semantics, matching the backend's `authenticated_within`: a future-dated stamp (clock skew)
 *     passes, an unreadable one fails closed;
 *  4. App Check → header, or fail closed on a throw. Unbounded here, deliberately — see
 *     {@link DEFAULT_WRITE_TIMEOUT_MS} on why nothing that waits needs it bounded;
 *  5. `POST /authn/session/login` with `credentials: 'include'` — required, because a cross-origin
 *     response's `Set-Cookie` is DISCARDED without it, so the whole mechanism silently does nothing.
 *     Admitted through the {@link EstablishSuiteSessionOptions.cancelHandle}: once the sign-in has
 *     ended it is never sent (`cancelled`), and the handle is checked after every leg as well, so an
 *     ended sign-in spends no mint either.
 */
export async function establishSuiteSession(
  options: EstablishSuiteSessionOptions,
): Promise<SuiteSessionEstablishOutcome> {
  const { user, rememberMe = true, cancelHandle } = options;
  if (user === null) return 'skipped';
  if (hasEnded(cancelHandle)) return 'cancelled';

  let token: string;
  let claims: Record<string, unknown>;
  let authTime: string;
  try {
    const result = await user.getIdTokenResult(true);
    token = result.token;
    claims = result.claims as unknown as Record<string, unknown>;
    authTime = result.authTime;
  } catch {
    return 'unavailable';
  }
  if (hasEnded(cancelHandle)) return 'cancelled';
  if (!token) return 'unavailable';

  if (claims?.[HANDOFF_CLAIM] === true) return 'skipped';
  if (!isFreshEnoughToEstablish(authTime)) return 'skipped';

  let appCheck: string | null;
  try {
    appCheck = await resolveAppCheckToken(options.appCheckToken);
  } catch {
    return 'unavailable';
  }

  // The checks above are courtesy — they save a mint and a request. THIS is the guarantee: `send`
  // admits the login through the handle in the same synchronous block that dispatches it.
  const response = await send(options, LOGIN_PATH, {
    appCheck,
    authorization: `Bearer ${token}`,
    body: JSON.stringify({ remember_me: rememberMe }),
    timeoutMs: options.timeoutMs ?? DEFAULT_WRITE_TIMEOUT_MS,
    cancelHandle,
  });
  if (response === NOT_SENT) return 'cancelled';
  return response !== null && response.ok ? 'established' : 'unavailable';
}

/**
 * Establish AFTER provisioning — the sign-up half of the chokepoint rule (AS-SUITE7).
 *
 * `POST /authn/session/login` refuses a verified token whose identity has no Givmo `user` row, by
 * design and uniformly with a malformed token (`suite_session_service.establish`, step 2): the
 * route is never an oracle for whether an account exists. Every sign-up ceremony in this estate
 * creates the Firebase user FIRST and provisions the row afterwards — `POST /admin/company`, the
 * request-access pivot's provisioning, `POST /users` on the OTP path — so an establish on the
 * sign-up's first emission is refused, {@link establishSuiteSession} swallows it as it must, and
 * the chokepoint's once-per-uid guard then never fires again: a freshly signed-up account held NO
 * suite session until a later real sign-in. The cure is client ordering, never a backend admission
 * — the refusal is the contract. Since AS-SUITE9 the admin chokepoint does not even spend that
 * emission-time request on a marked sign-up ceremony, so the attempt handed here is normally `null`
 * and this call is the sign-up's ONE request; a refused attempt still arrives when the emission
 * reached the chokepoint unmarked (a reload mid-ceremony).
 *
 * So a ceremony calls this ONCE, at the first point the row certainly exists, handing over
 * whatever attempt the chokepoint retained. Order of work:
 *
 *  1. wait for {@link EstablishSuiteSessionAfterProvisioningOptions.attempted} to settle. Waiting is
 *     the point — the chokepoint's request may still be in flight, and deciding before it answers
 *     could send a second request for a session the first is about to establish;
 *  2. `established` → answer `established` and send NOTHING. A session that already established
 *     must never establish twice: the second row would orphan the first cookie's row until the
 *     reaper, and two live rows for one browser is not a state the mechanism has;
 *  3. anything else — `unavailable`, `skipped`, `null`, or a rejection (the primitive never rejects,
 *     but the contract here is "never throws", so a foreign promise is tolerated) — run
 *     {@link establishSuiteSession} exactly once. Every before-the-wire refusal still applies: a
 *     handed-off session or a stale sign-in is `skipped` here too, at no request.
 *
 * Under a cancelled {@link EstablishSuiteSessionOptions.cancelHandle} it answers `cancelled` at
 * once, sending nothing and waiting on nothing: the sign-in this ceremony provisioned has ended, and
 * a sign-out already settled whatever it had on the wire (AS-SUITE11).
 *
 * Never throws; never blocks the ceremony that calls it — callers fire-and-forget and retain the
 * promise exactly as the chokepoint retains its own.
 */
export async function establishSuiteSessionAfterProvisioning(
  options: EstablishSuiteSessionAfterProvisioningOptions,
): Promise<SuiteSessionEstablishOutcome> {
  const { attempted, ...establish } = options;
  if (hasEnded(establish.cancelHandle)) return 'cancelled';
  if (attempted !== null) {
    let outcome: SuiteSessionEstablishOutcome | null;
    try {
      outcome = await attempted;
    } catch {
      outcome = null;
    }
    if (outcome === 'established') return 'established';
  }
  return establishSuiteSession(establish);
}

/**
 * Adopt an existing suite session on a property that booted signed-out.
 *
 * Call it from the auth-state chokepoint on the FIRST `null` emission, BEFORE `isReady` flips true —
 * a user who is signed in elsewhere must never see this property's door, not even for a frame. On a
 * credential the normal signed-in path runs: `signInWithCustomToken` re-enters the same
 * `onAuthStateChanged` listener with a user, so whatever that property does downstream (the charity
 * portal's `ensureBackendUser` bootstrap, for instance) happens through its own chokepoint, unchanged.
 *
 * Returns `null` for every "no" there is — no cookie, an unknown or expired one, a revoked user, the
 * routes dark, a refused `Origin`, a 429, a timeout, garbage JSON, a `signInWithCustomToken` that
 * rejects, an App Check mint that fails or outlasts its bound (AS-SUITE11 — see
 * {@link DEFAULT_ADOPT_TIMEOUT_MS}). The property then renders its door exactly as it does today.
 * **Never throws.**
 *
 * Refuses outright when `auth.currentUser` is already set: there is nothing to establish, the
 * module-direct door stays the door, and driving the handoff from a signed-in property would swap
 * the session out from under whoever is using it.
 *
 * **Persistence mirrors the ORIGIN door's Remember-me choice, and only ever narrows.** The handoff
 * answer echoes `remember_me` from the suite-session row; `false` means the person unchecked the box
 * at whatever door they actually signed in at, so this applies `session` scope BEFORE the exchange —
 * the canonical ordering `session.ts` documents, because a session established first can land at the
 * default scope before the switch takes effect. Without this, unchecking "remember me" on property A
 * would still leave a browser-restart-surviving session on property B, which on a shared computer is
 * the next person signed in as them.
 *
 * **`true`, absent, or anything that is not exactly `false` → nothing is set**, and the adopted
 * session inherits the property's configured scope exactly as the passkey path and the consumer OAuth
 * doors do (`agent_docs/auth.md`: *an omitted persistence means "decide nothing", not "pick a
 * default"*). Absent is the load-bearing case: it is what an older backend answers, and reading it as
 * `false` would punish every pre-fold deployment by narrowing sessions nobody asked to narrow. This is
 * the frozen-client tolerant-reader discipline pointed at our own web app.
 *
 * ⚠ It narrows on the SUCCESS path only. `setPersistence` is instance-wide, so narrowing at boot and
 * then finding no session would silently change what a subsequent OAuth sign-in — which passes no
 * persistence of its own — does at that property. Doing it after the token comes back keeps that
 * confined to the case where a transient session genuinely was adopted. (Residual, and it is
 * inherent rather than a defect here: after a transient adoption the instance stays `session`-scoped
 * for that page load, so a door sign-in that passes no persistence inherits it. The email door always
 * sets the scope explicitly, and a reload re-resolves from where the user was found.)
 *
 * A `setPersistence` that REJECTS fails closed — `null`, no adoption. Signing in anyway would
 * establish exactly the `local`-scoped session this is here to prevent, and the cost of failing is a
 * visible door the person can sign in at.
 */
export async function adoptSuiteSession(
  options: AdoptSuiteSessionOptions,
): Promise<UserCredential | null> {
  const { auth } = options;
  if (auth.currentUser !== null) return null;
  const timeoutMs = options.timeoutMs ?? DEFAULT_ADOPT_TIMEOUT_MS;

  // Bounded: this mint is in front of first paint, and the SDK bounds none of it.
  let appCheck: string | null;
  try {
    appCheck = await resolveAppCheckToken(options.appCheckToken, timeoutMs);
  } catch {
    return null;
  }

  const response = await send(options, HANDOFF_PATH, { appCheck, body: '{}', timeoutMs });
  if (response === null || response === NOT_SENT || !response.ok) return null;

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return null;
  }

  // ⚠ THE CUSTOM TOKEN IS A BEARER CREDENTIAL. It lives only as this local, between the read and the
  // exchange — never on an object, an error, or anything that could serialise it. Same discipline
  // `passkeys.ts` keeps, reached the same way: by giving the value no lifetime rather than by hiding
  // it. `suite-session.test.ts` pins it against both `JSON.stringify(err)` and the SPREAD form,
  // because a value on an own enumerable property is invisible to the first.
  const envelope = body as {
    session?: unknown;
    custom_token?: unknown;
    remember_me?: unknown;
  } | null;
  if (envelope === null || typeof envelope !== 'object') return null;
  if (envelope.session !== true) return null;
  const customToken = envelope.custom_token;
  if (typeof customToken !== 'string' || customToken.length === 0) return null;

  // Exactly `false`, never truthiness. Absent, `true`, `null`, `"false"` and every other shape mean
  // "no instruction to narrow" — see the docstring on why absent must not read as `false`.
  if (envelope.remember_me === false) {
    try {
      await applySessionPersistence(auth, 'session');
    } catch {
      return null;
    }
  }

  try {
    return await signInWithCustomToken(auth, customToken);
  } catch {
    return null;
  }
}

/**
 * End this browser's suite session.
 *
 * Call it BEFORE the local `firebaseSignOut`, on every ordinary sign-out path. Before, for two
 * reasons: the route needs the cookie rather than a Firebase user, and after the local sign-out the
 * property may already have torn down the state that got it here. Getting the order backwards leaves
 * a window in which the property can silently re-establish the session the user just ended.
 *
 * **Hand it the ending sign-in's cancel handle** (AS-SUITE11). The release cancels it before doing
 * anything else, and is SENT only once every login that handle admitted has settled — a release
 * that overtook a login still on the wire would delete nothing, and the login would then set the
 * cookie. Its own App Check mint runs in parallel with that wait: the token has no ordering to keep,
 * so its bound overlaps the wait instead of following it. Worst case, ≈ 16 s to returning: a login
 * sent just before the click and black-holed (settled by its 8 s write timeout) while the mint runs
 * (≤ 8 s), then a black-holed release (8 s) — see {@link DEFAULT_WRITE_TIMEOUT_MS}.
 *
 * Idempotent, silent, and it never rejects — a sign-out must not fail because a convenience layer
 * did. Callers `await` it (the ordering is the point) and ignore the result.
 *
 * **Web-suite only, by ratified design** (plan §9, D-AS-13 2026-08-27). It does not revoke the
 * account's Firebase sessions: a tab already open on another property keeps its live ID token for up
 * to its remaining hour, and the phone is untouched. Account-wide sign-out is a separate, explicit
 * control — making every ordinary sign-out account-wide would sign someone out of their phone for
 * closing a browser tab.
 */
export async function releaseSuiteSession(options: ReleaseSuiteSessionOptions): Promise<void> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_WRITE_TIMEOUT_MS;
  // Cancel first, synchronously, before any await: from here no login of the ending sign-in starts.
  // (`cancel` never rejects; the swallow is for a structural look-alike, which admitted nothing.)
  const loginsSettled = Promise.resolve(options.cancelHandle?.cancel()).then(noop, noop);
  // Mint now, in parallel with that wait — bounded, because a sign-out waits on it.
  const attested = resolveAppCheckToken(options.appCheckToken, timeoutMs).then(
    (token) => ({ token }),
    () => null,
  );

  await loginsSettled;
  const minted = await attested;
  // Fail closed, as everywhere else: an unattested release is not sent. The local sign-out still
  // runs, so the user's own tab is signed out either way, and the suite row lapses at its own TTL.
  if (minted === null) return;

  await send(options, LOGOUT_PATH, { appCheck: minted.token, body: '{}', timeoutMs });
}

// ── internals ───────────────────────────────────────────────────────────────────────────────────

/**
 * Whether an `auth_time` is recent enough to start a suite session.
 *
 * `IdTokenResult.authTime` is a UTC date STRING with SECOND resolution — the SDK renders it through
 * `toUTCString()`, so sub-second precision is gone by the time it reaches here. That truncation is
 * always conservative: a floored `auth_time` can only make elapsed look LARGER, so this can refuse
 * up to a second earlier than the backend would and never a second later. It therefore never sends a
 * request the backend would reject as stale, which is the direction that matters.
 *
 * An unparseable or absent one fails CLOSED — absence of evidence is not evidence of freshness, and
 * the backend fails closed on a missing `auth_time` for the same reason. A future-dated stamp
 * passes: clock skew is not an attack, and the backend's elapsed-time comparison treats it
 * identically.
 */
function isFreshEnoughToEstablish(authTime: string | undefined, now = Date.now()): boolean {
  if (typeof authTime !== 'string' || authTime.length === 0) return false;
  const parsed = Date.parse(authTime);
  if (Number.isNaN(parsed)) return false;
  return now - parsed <= MAX_AUTH_AGE_MS;
}

/**
 * Resolve the App Check token, letting a mint failure PROPAGATE so each caller can fail closed.
 *
 * With `boundMs`, a mint still pending at the bound is a failed mint (AS-SUITE11). A race is the
 * right tool HERE and only here: an abandoned mint has no effect anyone must order against — if it
 * answers later, it merely warms App Check's own cache — whereas an abandoned LOGIN can still set a
 * cookie, which is why the login's wait is a cancel handle and never a timer.
 */
async function resolveAppCheckToken(
  source: SuiteAppCheckTokenSource | undefined,
  boundMs?: number,
): Promise<string | null> {
  if (source === undefined) return null;
  const token = boundMs === undefined ? await source() : await withinBound(source(), boundMs);
  return token !== null && token !== undefined && token.length > 0 ? token : null;
}

/** `pending`, or a rejection once `ms` has elapsed first. The timer never outlives the answer. */
function withinBound<T>(pending: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const bound = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error('App Check mint exceeded its bound.')), ms);
  });
  return Promise.race([pending, bound]).finally(() => clearTimeout(timer));
}

function noop(): void {}

/**
 * Whether the handle's sign-in has ended. A call rather than an inline `handle?.cancelled` because
 * the flag changes across an `await`, and TypeScript's narrowing would pin the second read to the
 * first one's answer.
 */
function hasEnded(handle: SuiteSessionCancelHandle | undefined): boolean {
  return handle?.cancelled === true;
}

/** What {@link send} answers when a cancel handle refused the request. Nothing reached the wire. */
const NOT_SENT = Symbol('suite-session request not sent');

interface SendOptions {
  appCheck: string | null;
  authorization?: string;
  body: string;
  timeoutMs: number;
  /** The login's cancel handle. Only an establish passes one; the release and the handoff are never gated. */
  cancelHandle?: SuiteSessionCancelHandle;
}

/** The gate of a handle this module never minted: closed, so a look-alike admits nothing. */
const LOOK_ALIKE_GATE: SendGate = { cancelled: true, onWire: new Set() };

/**
 * POST one suite-session route and return the `Response`, `null` when the request could not be
 * completed at all (no `fetch`, a transport failure, the timeout), or {@link NOT_SENT} when its
 * cancel handle refused to let it start.
 *
 * A `Request` is constructed explicitly rather than passing `(url, init)`, so what the tests assert
 * on is the object the platform will actually send — `credentials`, the headers, the method and the
 * body as the browser resolved them, not an init bag that could differ from it.
 */
async function send(
  transport: SuiteSessionTransport,
  path: string,
  { appCheck, authorization, body, timeoutMs, cancelHandle }: SendOptions,
): Promise<Response | null | typeof NOT_SENT> {
  const doFetch = transport.fetch ?? globalThis.fetch;
  if (typeof doFetch !== 'function') return null;

  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (appCheck !== null) headers.set(APP_CHECK_HEADER, appCheck);
  if (authorization !== undefined) headers.set('Authorization', authorization);

  const controller = new AbortController();
  let request: Request;
  try {
    // `credentials: 'include'` on ALL THREE. Every suite property is cross-ORIGIN with the API host
    // (same-SITE, which is what lets `SameSite=Strict` send the cookie at all), and a cross-origin
    // fetch neither sends a cookie nor stores a `Set-Cookie` without it. Dropping it does not error;
    // it makes the whole mechanism a no-op that looks exactly like the backend being dark.
    request = new Request(joinUrl(transport.baseUrl, path), {
      method: 'POST',
      credentials: 'include',
      headers,
      body,
      signal: controller.signal,
    });
  } catch {
    return null;
  }

  // AS-SUITE11 ⚠ ADMISSION AND DISPATCH ARE ONE SYNCHRONOUS BLOCK — from this check to the `fetch()`
  // call below there is no `await`, and there must never be one. That is the whole reason a
  // cancelled handle's `cancel()` can promise that no login slips past it: it takes its list in one
  // block too, so every login is either on that list or refused here. See `SuiteSessionCancelHandle`.
  const gate =
    cancelHandle === undefined ? null : (SEND_GATES.get(cancelHandle) ?? LOOK_ALIKE_GATE);
  if (gate !== null && gate.cancelled) return NOT_SENT;

  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const inFlight = doFetch(request);
    if (gate !== null) {
      gate.onWire.add(inFlight);
      const drop = () => void gate.onWire.delete(inFlight);
      void inFlight.then(drop, drop);
    }
    return await inFlight;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** `baseUrl` + `path`, tolerating a trailing slash exactly as `@givmo/api-client`'s `resolveBaseUrl` does. */
function joinUrl(baseUrl: string, path: string): string {
  const base = (baseUrl ?? '').trim();
  return `${base.endsWith('/') ? base.slice(0, -1) : base}${path}`;
}
